'use client';

import Script from 'next/script';
import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import type { TurnstileMode } from './selectKeys';
import { VerifyRouteParams } from './verifyRequest';

declare global {
  interface Window {
    turnstile: {
      render: (
        container: Element,
        options: Record<string, unknown>
      ) => string;
      remove: (widgetId: string) => void;
    };
    cj?: {
      navigate: (destinationUrl: string) => void;
    };
  }
}

// Minimum splash time before we hand the user to the destination. Matches the
// non-Turnstile DEFAULT_REDIRECT_TIMEOUT so users on the gated and non-gated
// invisible paths see the splash for the same duration.
//
// Deliberately not applied in visible mode: the user clicks through the
// widget themselves, so "experience unchanged" doesn't apply — holding them
// for an extra 1.5s after the click would feel broken.
const MIN_SPLASH_MS = 1500;

// Upper bound on how long we wait for Turnstile to complete before we give up
// and surface a generic error. Cloudflare may show an interactive challenge to
// visitors it's uncertain about, so this needs to be comfortably larger than
// the invisible-challenge wall time. The same value is used for both modes;
// visible-mode users who simply don't interact will eventually time out here.
const TURNSTILE_SAFETY_TIMEOUT_MS = 30_000;

interface Props {
  siteKey: string;
  mode: TurnstileMode;
  routeParams: VerifyRouteParams;
}

// Navigates the browser to destinationUrl using CJ's tracking script when the
// destination is a CJ publisher, otherwise a normal location change. If
// isCjPublisher is true but window.cj is unavailable we deliberately throw
// rather than falling back — bypassing CJ silently would lose attribution, and
// we'd rather surface the generic error than do the wrong thing.
//
// isCjPublisher is decided by the verify route from the server-side fetched
// destination, not by the page render, so there's no risk of stale CJ status
// if the underlying data changed between render and verify.
function navigateTo(destinationUrl: string, isCjPublisher: boolean): void {
  if (isCjPublisher) {
    if (typeof window.cj?.navigate !== 'function') {
      throw new Error('CJ navigate unavailable');
    }
    window.cj.navigate(destinationUrl);
    return;
  }
  window.parent.location.href = destinationUrl;
}

const TurnstileComponent: React.FC<Props> = ({ siteKey, mode, routeParams }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountTimeRef = useRef<number>(Date.now());
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const floorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasResolvedRef = useRef<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Arm the safety timeout on mount; disarm it on unmount or on resolution.
  // If it fires, we surface a generic error — identical user-visible outcome
  // to any other Turnstile failure so we don't leak timing signals.
  useEffect(() => {
    safetyTimerRef.current = setTimeout(() => {
      if (hasResolvedRef.current) return;
      setError(new Error('Turnstile safety timeout'));
    }, TURNSTILE_SAFETY_TIMEOUT_MS);

    return () => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      if (floorTimerRef.current) clearTimeout(floorTimerRef.current);
    };
  }, []);

  // Surfacing errors via throw lets Next's error boundary render the generic
  // error page (see app/redirect/error.tsx). Every failure mode — challenge
  // failure, expiry, script load error, server rejection, safety timeout —
  // funnels through here so the attacker-visible behaviour is uniform.
  if (error) throw error;

  const handleScriptLoad = () => {
    if (!containerRef.current) return;

    // Fail fast on an empty or obviously-malformed site key rather than
    // letting Cloudflare's turnstile.render throw synchronously with no
    // callback — that path has no observability hook, and the only thing
    // that would eventually surface the failure is the 30s safety timer.
    //
    // An empty string almost always means the secret didn't make it into
    // the pod's mounted `/data/secret/` — usually a missing ESO property
    // selector in lodge's values.yaml. Surfacing the error immediately
    // here lets an operator see it in RUM instead of waiting out a 30s
    // hang and seeing only a generic Cloudflare throw in the console.
    if (!siteKey) {
      setError(new Error('Turnstile site key is not configured'));
      return;
    }

    // Wrap the render call so any synchronous throw from Cloudflare's
    // client (bad site key format, missing dependency, etc.) routes into
    // our normal error flow rather than bubbling up as an uncaught
    // exception the user never sees.
    try {
      window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        // Cloudflare's size parameter: 'invisible' hides the widget entirely;
        // 'normal' renders the default-sized interactive widget. The site key
        // must be provisioned in the matching mode in the Cloudflare dashboard
        // for this to behave correctly (see selectKeys.ts).
        size: mode === 'visible' ? 'normal' : 'invisible',
        callback: async (token: string) => {
          try {
            const response = await fetch('/redirect/turnstile-verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                token,
                currentUrl: window.location.href,
                routeParams,
              }),
            });
            if (!response.ok) {
              throw new Error('Turnstile verification failed');
            }
            const { destinationUrl, isCjPublisher } = await response.json();
            if (!destinationUrl) {
              throw new Error('Turnstile verification returned no destination');
            }

            hasResolvedRef.current = true;
            if (safetyTimerRef.current) {
              clearTimeout(safetyTimerRef.current);
              safetyTimerRef.current = null;
            }

            // Invisible mode: enforce a minimum splash duration so gated and
            // non-gated users see the same 1.5s splash. Without this a fast
            // challenge (~200ms) would produce a noticeably snappier experience
            // on gated product types — an observable difference that also
            // hints at which paths are Turnstile-gated.
            //
            // Visible mode: navigate as soon as the verify succeeds. The user
            // has already interacted with a visible widget, so there's no
            // timing-uniformity property to preserve, and adding a post-click
            // delay feels broken.
            if (mode === 'visible') {
              try {
                navigateTo(destinationUrl, Boolean(isCjPublisher));
              } catch (navErr) {
                setError(
                  navErr instanceof Error
                    ? navErr
                    : new Error('Navigation failed')
                );
              }
              return;
            }

            const elapsed = Date.now() - mountTimeRef.current;
            const delay = Math.max(0, MIN_SPLASH_MS - elapsed);
            floorTimerRef.current = setTimeout(() => {
              try {
                navigateTo(destinationUrl, Boolean(isCjPublisher));
              } catch (navErr) {
                setError(
                  navErr instanceof Error
                    ? navErr
                    : new Error('Navigation failed')
                );
              }
            }, delay);
          } catch (err) {
            setError(
              err instanceof Error ? err : new Error('Verification failed')
            );
          }
        },
        'error-callback': () => {
          setError(new Error('Turnstile challenge failed'));
        },
        'expired-callback': () => {
          setError(new Error('Turnstile challenge expired'));
        },
      });
    } catch (renderErr) {
      setError(
        renderErr instanceof Error
          ? renderErr
          : new Error('Turnstile render failed')
      );
    }
  };

  // Visible-mode container is positioned below the spinner via fixed
  // positioning so the component tree (TurnstileGate → RedirectComponent +
  // TurnstileComponent, with DisplayComponent rendered as a later sibling of
  // TurnstileGate) doesn't need to be restructured. Invisible-mode widget
  // has no visible footprint so the class is a no-op there.
  const containerClassName =
    mode === 'visible'
      ? 'turnstile-container turnstile-container--visible'
      : 'turnstile-container turnstile-container--invisible';

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
        onError={() => setError(new Error('Failed to load Turnstile script'))}
      />
      <div className={containerClassName} data-testid="turnstile-container">
        <div ref={containerRef} />
      </div>
    </>
  );
};

export default TurnstileComponent;
